import { useTranslation } from 'react-i18next';
import { Button, FormField, Input } from '@/components/ui';
import type { RoleKeyCreateFormVM } from './useRoleKeyCreateForm';
import styles from './RoleKeyCreateForm.module.css';

/**
 * EAA-1817: the "Tạo vai trò chuẩn" form, extracted out of
 * `NoRoleKeysEmptyState` so it has a second entry point (the RoleMapPage
 * overflow menu) without a second copy of the same three fields — see
 * `RoleMapPage.tsx`'s `moreActionsAria` menu for why one was needed.
 */
export function RoleKeyCreateForm({ form }: { form: RoleKeyCreateFormVM }) {
    const { t } = useTranslation();
    return (
        <form className={`portal-admin-form ${styles.roleKeyForm}`} onSubmit={form.onSubmit}>
            {/* EAA-1804: raw `<label><span className="portal-field-label">…` field
                chrome → kit `FormField` (covers the label/required/hint/error chrome,
                not just the control — see root CLAUDE.md's "field chrome" rule). The
                description field's "(optional)" suffix has no dedicated `FormField`
                prop, so it stays folded into `label` as extra content. `form.error`
                is a FORM-level error (one line under all three fields, not any single
                field's own), so it is deliberately left outside `FormField`. */}
            <FormField label={t('console.roleCoverage.noRoleKeys.form.keyLabel')} required>
                <Input
                    placeholder={t('console.roleCoverage.noRoleKeys.form.keyPlaceholder')}
                    value={form.form.key}
                    onChange={(e) => form.setForm({ ...form.form, key: e.target.value })}
                    required
                />
            </FormField>
            <FormField label={t('console.roleCoverage.noRoleKeys.form.nameLabel')} required>
                <Input
                    placeholder={t('console.roleCoverage.noRoleKeys.form.namePlaceholder')}
                    value={form.form.name}
                    onChange={(e) => form.setForm({ ...form.form, name: e.target.value })}
                    required
                />
            </FormField>
            <FormField
                label={
                    <>
                        {t('console.roleCoverage.noRoleKeys.form.descriptionLabel')}{' '}
                        <span className="portal-field-optional">
                            ({t('console.roleCoverage.noRoleKeys.form.optionalLabel')})
                        </span>
                    </>
                }
            >
                <Input
                    value={form.form.description}
                    onChange={(e) => form.setForm({ ...form.form, description: e.target.value })}
                />
            </FormField>

            {form.error && <p className="portal-admin-error">{form.error}</p>}

            <div className={styles.roleKeyFormActions}>
                {/* EAA-1804: was `className="portal-btn"` / `"portal-btn portal-btn-primary"`
                    — corrected to plain `<Button>` and `type="primary"`; `htmlType`
                    (not `type`, which antd reserves for the visual variant) keeps the
                    submit wired to this <form>'s onSubmit. See AccountDetailModal.tsx. */}
                <Button onClick={() => form.setOpen(false)}>{t('common.cancel')}</Button>
                <Button type="primary" htmlType="submit" disabled={form.pending}>
                    {form.pending
                        ? t('console.roleCoverage.noRoleKeys.form.submitPending')
                        : t('console.roleCoverage.noRoleKeys.createButton')}
                </Button>
            </div>
        </form>
    );
}
